import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image, File, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const ALLOWED_TYPES = {
  'application/pdf': { ext: '.pdf', icon: FileText, color: '#EF4444' },
  'image/jpeg': { ext: '.jpg', icon: Image, color: '#10B981' },
  'image/jpg': { ext: '.jpg', icon: Image, color: '#10B981' },
  'image/png': { ext: '.png', icon: Image, color: '#10B981' },
  'image/svg+xml': { ext: '.svg', icon: Image, color: '#10B981' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: '.docx', icon: FileText, color: '#2563EB' },
  'application/msword': { ext: '.doc', icon: FileText, color: '#2563EB' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: '.xlsx', icon: FileText, color: '#059669' },
  'application/vnd.ms-excel': { ext: '.xls', icon: FileText, color: '#059669' },
};

const FileUpload = ({ 
  clientId, 
  taskId = null,
  uploaderRole = 'client',
  uploaderName = null,
  onUploadComplete,
  onError,
  multiple = true,
  compact = false,
  allowedMimeTypes = null // Array of MIME types, e.g., ['image/png', 'application/pdf']
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Use allowedMimeTypes if provided, otherwise use default ALLOWED_TYPES
    const allowedTypes = allowedMimeTypes && allowedMimeTypes.length > 0 
      ? allowedMimeTypes 
      : Object.keys(ALLOWED_TYPES);
    
    if (!allowedTypes.includes(file.type)) {
      const allowedNames = allowedTypes.map(type => {
        const info = ALLOWED_TYPES[type];
        return info ? info.ext : type.split('/')[1].toUpperCase();
      }).join(', ');
      return `File type not allowed. Please upload: ${allowedNames}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File ${file.name} is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    }
    return null;
  };

  const uploadFile = async (file) => {
    const fileId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      setUploadProgress(prev => ({ ...prev, [fileId]: { name: file.name, progress: 0, status: 'initiating' } }));

      // Step 1: Initiate upload
      const initiateResponse = await fetch(
        `${BACKEND_URL}/api/clients/${clientId}/files/initiate-upload`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_name: file.name,
            mime_type: file.type,
            task_id: taskId,
            uploader_role: uploaderRole,
            uploader_name: uploaderName
          })
        }
      );

      if (!initiateResponse.ok) {
        throw new Error('Failed to initiate upload');
      }

      const { uploadUrl, fileId: serverFileId, storageKey } = await initiateResponse.json();

      setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], progress: 25, status: 'uploading' } }));

      // Step 2: Upload to Firebase
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Firebase upload failed:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          error: errorText,
          uploadUrl: uploadUrl.substring(0, 100) + '...'
        });
        throw new Error(`Failed to upload file to storage: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], progress: 75, status: 'confirming' } }));

      // Step 3: Confirm upload
      const confirmResponse = await fetch(
        `${BACKEND_URL}/api/clients/${clientId}/files/${serverFileId}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storage_key: storageKey,
            size_bytes: file.size
          })
        }
      );

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm upload');
      }

      const result = await confirmResponse.json();

      setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], progress: 100, status: 'complete' } }));
      setUploadedFiles(prev => [...prev, result.file]);

      // Remove from progress after 2 seconds
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }, 2000);

      if (onUploadComplete) {
        onUploadComplete(result.file);
      }

      return result.file;

    } catch (error) {
      console.error('Upload error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        file: file.name,
        size: file.size,
        type: file.type
      });
      setUploadProgress(prev => ({ ...prev, [fileId]: { ...prev[fileId], status: 'error', error: error.message } }));
      
      if (onError) {
        onError(error.message);
      }

      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }, 5000);

      throw error;
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    
    // Validate all files first
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        if (onError) onError(error);
        return;
      }
    }

    setUploading(true);

    try {
      if (multiple) {
        await Promise.all(fileArray.map(file => uploadFile(file)));
      } else {
        await uploadFile(fileArray[0]);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const onButtonClick = () => {
    fileInputRef.current.click();
  };

  // Get accept attribute based on allowed types
  const getAcceptString = () => {
    if (allowedMimeTypes && allowedMimeTypes.length > 0) {
      return allowedMimeTypes.join(',');
    }
    return Object.keys(ALLOWED_TYPES).join(',');
  };

  if (compact) {
    return (
      <div style={{ width: '100%' }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          onChange={handleChange}
          accept={getAcceptString()}
          style={{ display: 'none' }}
        />
        
        <button
          onClick={onButtonClick}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '12px',
            background: uploading ? '#9CA3AF' : '#4D7CFE',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: uploading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <Upload size={18} />
          {uploading ? 'Uploading...' : 'Choose Files'}
        </button>

        {/* Progress indicators */}
        {Object.entries(uploadProgress).map(([id, info]) => (
          <div key={id} style={{ marginTop: '8px', padding: '8px', background: '#F3F4F6', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '12px', color: '#374151', fontWeight: '500' }}>{info.name}</span>
              <span style={{ fontSize: '11px', color: '#6B7280' }}>{info.progress}%</span>
            </div>
            <div style={{ width: '100%', height: '4px', background: '#E5E7EB', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                width: `${info.progress}%`,
                height: '100%',
                background: info.status === 'error' ? '#EF4444' : '#4D7CFE',
                transition: 'width 0.3s'
              }} />
            </div>
            {info.status === 'error' && (
              <p style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>{info.error}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        onChange={handleChange}
        accept={getAcceptString()}
        style={{ display: 'none' }}
      />
      
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={onButtonClick}
        style={{
          border: `2px dashed ${dragActive ? '#4D7CFE' : '#D1D5DB'}`,
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          background: dragActive ? '#EFF6FF' : '#F9FAFB',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        <Upload size={48} color={dragActive ? '#4D7CFE' : '#9CA3AF'} style={{ margin: '0 auto 16px' }} />
        <p style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
          {dragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
        </p>
        <p style={{ fontSize: '14px', color: '#6B7280' }}>
          PDF, images, Word, Excel (max {MAX_FILE_SIZE / (1024 * 1024)}MB each)
        </p>
      </div>

      {/* Upload progress */}
      {Object.entries(uploadProgress).length > 0 && (
        <div style={{ marginTop: '16px' }}>
          {Object.entries(uploadProgress).map(([id, info]) => (
            <div key={id} style={{ marginBottom: '12px', padding: '12px', background: '#F3F4F6', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {info.status === 'complete' ? (
                    <CheckCircle size={20} color="#10B981" />
                  ) : info.status === 'error' ? (
                    <AlertCircle size={20} color="#EF4444" />
                  ) : (
                    <FileText size={20} color="#4D7CFE" />
                  )}
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{info.name}</span>
                </div>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                  {info.status === 'complete' ? 'Complete' : info.status === 'error' ? 'Failed' : `${info.progress}%`}
                </span>
              </div>
              {info.status !== 'complete' && info.status !== 'error' && (
                <div style={{ width: '100%', height: '6px', background: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${info.progress}%`,
                    height: '100%',
                    background: '#4D7CFE',
                    transition: 'width 0.3s'
                  }} />
                </div>
              )}
              {info.status === 'error' && (
                <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>{info.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
